import {
  ColumnDefinition,
  MenuItem,
  NotificationService,
  type Option,
  type Options,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiDataTableComponent,
  OuiGridComponent,
  OuiMenuComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiSymbolComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, computed, inject, Input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import currenciesData from '../../../assets/currencies.json';
import { DownloadButtonComponent } from '../../components/download-button/download-button.component';
import {
  OrderDetail,
  OrderItem,
  type OrderStatusKey,
} from '../../models/order.models';
import { OrderStatuses } from '../../services/order/order.constants';
import { OrderService } from '../../services/order/order.service';
import { BasePageComponent } from '../base/base.component';

type CurrencyConfig = {
  code?: string;
  symbol?: string;
  decimal_digits?: number;
};

const currencyEntries = ((currenciesData as { currencies?: CurrencyConfig[] })?.currencies || [])
  .map((currency) => ({
    code: (currency.code || '').trim().toUpperCase(),
    symbol: currency.symbol || '',
    decimals:
      typeof currency.decimal_digits === 'number' ? currency.decimal_digits : 2,
  }))
  .filter((currency) => currency.code);

const currencySymbols = Object.fromEntries(
  currencyEntries.map((currency) => [currency.code, currency.symbol || currency.code]),
);

const currencyDecimals = Object.fromEntries(
  currencyEntries.map((currency) => [currency.code, currency.decimals]),
);

@Component({
  selector: 'app-order-details',
  imports: [
    OuiCenterComponent,
    OuiStackComponent,
    OuiBoxComponent,
    OuiDataTableComponent,
    OuiTextComponent,
    DownloadButtonComponent,
    OuiGridComponent,
    DatePipe,
    OuiRowComponent,
    OuiButtonComponent,
    RouterLink,
    OuiSymbolComponent,
    OuiMenuComponent,
  ],
  templateUrl: './order-details.component.html',
  standalone: true,
})
export class OrderDetailsPage extends BasePageComponent {
  private readonly notifications = inject(NotificationService);
  private readonly orders = inject(OrderService);
  private readonly notification = inject(NotificationService);

  @Input()
  set orderNumber(orderNumber: number) {
    this.getOrder(orderNumber);
  }

  constructor() {
    super();
  }

  showDialog = signal<boolean>(false);
  dialogError = signal<string | undefined>(undefined);
  order?: OrderDetail;
  // Signal holding the human-readable status label
  statusLabel = computed(() => this.selectedStatus()?.label);
  orderItems!: OuiDatasource<OrderItem>;
  hasBulkShipping = signal(false);

  gridLayout = computed(() =>
    this.hasBulkShipping()
      ? "'heading heading' 'order shipping' 'order bulkShipping' 'products pricing'"
      : "'heading heading' 'order shipping' 'products pricing'",
  );

  orderItemColumns!: ColumnDefinition<OrderItem>[];
  syncingOrder = signal<boolean>(false);
  isMexicoRegion = computed(
    () => (this.regions.selectedRegionId?.() ?? 0) === 1,
  );
  permissionToManageOrders = this.permissionTo('order:manage');

  // TODO: Update this list when the order statuses API becomes available.
  statusOptions = computed<Options<string>>(() => [
    { label: 'In Progress', value: 'InProgress' },
    { label: 'Awaiting Payment', value: 'AwaitingPayment' },
    { label: 'Awaiting Exchange', value: 'AwaitingExchange' },
    { label: 'Ready For Fulfillment', value: 'ReadyForFulfillment' },
    { label: 'In Picking', value: 'InPicking' },
    { label: 'Staged For Delivery', value: 'StagedForDelivery' },
    { label: 'Partially Shipped', value: 'PartiallyShipped' },
    { label: 'Shipped', value: 'Shipped' },
    { label: 'Cancelled', value: 'Cancelled' },
    { label: 'Closed', value: 'Closed' },
    { label: 'Completed', value: 'Completed' },
  ]);

  // Holds the raw selected status code; derive validated selection via computed
  selectedStatusValue = signal<string | undefined>(undefined);

  selectStatus(status: Option<string>) {
    this.selectedStatus.set(status);
  }

  selectedStatus = signal<Option<string> | null>(null, {
    equal: (a, b) => a?.label === b?.label,
    debugName: 'Selected Status',
  });

  menuItems = computed<MenuItem[]>(() => {
    // Do not expose dropdown until a status has been selected (or single auto-selected).

    return this.statusOptions().map((r) => ({
      label: r.label,
      action: () => this.selectStatus(r),
    }));
  });

  private updateStatusLabel(status: string | undefined) {
    if (!status) {
      this.selectedStatus.set(null);
      return;
    }
    const match = this.statusOptions().find(
      (o) => o.value === status || o.label === status,
    );
    this.selectedStatus.set(match ? match : null);
  }

  getOrder(orderNumber: number) {
    this.orders.getOrderById(orderNumber).subscribe((order) => {
      this.order = order;
      this.updateStatusLabel(order.status);
      this.orderItems = new OuiDatasource<OrderItem>(order.items);
      this.hasBulkShipping.set(!!order.bulkShippingMethod);

      this.orderItemColumns = [
        {
          columnDef: 'sku',
          header: 'SKU',
        },
        {
          columnDef: 'productName',
          header: 'Product Name',
        },
        {
          columnDef: 'quantity',
          header: 'Qty.',
        },
        {
          columnDef: 'unitPrice',
          header: 'Unit Price',
          cell: (element) =>
            this.formatOrderTotalAmount(element.unitPrice, order.currencyCode),
        },
        {
          columnDef: 'totalPrice',
          header: 'Total',
          cell: (element) =>
            this.formatOrderTotalAmount(element.totalPrice, order.currencyCode),
        },
      ];
    });
  }

  protected formatOrderTotalAmount(amount: number, currencyCode?: string) {
    const code = (currencyCode || '').toUpperCase();
    const symbol = currencySymbols[code] || code || '$';
    const decimals = currencyDecimals[code] ?? 2;

    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount || 0);

    return `${symbol}${formattedValue}`;
  }

  syncOrder(orderNumber: number) {
    this.syncingOrder.set(true);
    this.orders.syncOrderDate(orderNumber).subscribe({
      next: () => {
        this.notification.showSnackbar({
          message: `Oder No. ${orderNumber} synced successfully.`,
          symbolName: 'task_alt',
          variant: 'success',
        });
        this.syncingOrder.set(false);
      },
    });
  }

  updateOrderStatusId(orderNumber: number) {
    const status = this.selectedStatus()?.value;
    if (!status) {
      this.notifications.showAlert({
        message: 'Please select a status before updating.',
        variant: 'warn',
        symbolName: 'info',
      });
      return;
    }

    this.orders.updateOrderStatus(orderNumber, status).subscribe({
      next: () => {
        // Update local order signal if loaded
        const current = this.order;
        if (current) {
          this.order = { ...(current as OrderDetail), status } as OrderDetail;
        }
        this.notification.showSnackbar({
          message: 'Order Status Updated Successfully',
          variant: 'success',
          symbolName: 'shield_person',
        });
        this.notification.hideAlert();
        // Redirect to Orders page after successful update
        this.router.navigate(['/orders']);
      },
      error: (response: HttpErrorResponse) => {
        const { error } = response;
        const errorMessage =
          error && Array.isArray(response.error)
            ? error
                .map((err: { error_message: never }) => err.error_message)
                .join(', ')
            : 'An unexpected error occurred.';
        this.dialogError.set(errorMessage);
        this.showDialog.set(false);
      },
      complete: () => {
        this.dialogError.set(undefined);
        this.showDialog.set(false);
      },
    });
  }

  cancelStatusUpdate() {
    this.router.navigate(['/orders']);
  }

  protected getStatusLabel(status: string) {
    return this.getStatus(status)?.label || status;
  }

  protected getStatusColor(status: string) {
    return this.getStatus(status)?.color;
  }

  private getStatus(status: string) {
    return (
      OrderStatuses[status as OrderStatusKey] ||
      Object.values(OrderStatuses).find(
        (option) => option.value === status || option.label === status,
      )
    );
  }

  downloadErpLogs(orderId: number): Observable<HttpResponse<Blob>> {
    return this.orders.downloadErpLogs(orderId);
  }

  getErpLogDownloadFilename(orderId?: number) {
    return `ErpOrderLogs_${orderId || 'Unknown'}_${new Date().toISOString().split('T')[0]}.xlsx`;
  }
}
